import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import AuthPageShell from './components/AuthPageShell';
import { useSiteSettings } from './SiteSettingsContext';
import { brandNameFromSettings, brandLogoSrcFromSettings } from './siteBrand';
import PasswordInput from './components/PasswordInput';

export default function ResetPasswordPage() {
  const { settings: site } = useSiteSettings();
  const auth = site.auth_copy || {};
  const brandName = brandNameFromSettings(site);
  const brandLogo = brandLogoSrcFromSettings(site);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = (searchParams.get('token') || '').replace(/\s+/g, '');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMsg('');
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    setBusy(true);
    try {
      const r = await fetch('/api/customer.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'confirm_password_reset', token, new_password: password }),
      });
      const d = await r.json();
      if (!r.ok || !d.success) {
        setError(d.error || 'Could not reset password.');
        return;
      }
      setMsg('Password updated. You can sign in now.');
      window.setTimeout(() => navigate('/login', { replace: true }), 1500);
    } catch {
      setError('Something went wrong. Try again.');
    } finally {
      setBusy(false);
    }
  };

  if (!token) {
    return (
      <AuthPageShell
        pageTitle="Invalid link"
        asideTitle={auth.reset_invalid_aside_title || 'Link expired or invalid'}
        asideText={
          auth.reset_invalid_aside_text ||
          'Password reset links expire after one hour. Request a new link to continue.'
        }
        footer={
          <div className="text-center">
            <Link to="/forgot-password" className="btn btn-primary rounded-pill">
              Request a new link
            </Link>
          </div>
        }
      >
        <p className="text-muted mb-0">
          {auth.reset_invalid_body || 'This reset link is not valid. Please request a new one.'}
        </p>
      </AuthPageShell>
    );
  }

  return (
    <AuthPageShell
      pageTitle="Choose new password"
      asideTitle={auth.reset_aside_title || 'Choose a new password'}
      asideText={auth.reset_aside_text || 'Use at least 8 characters. After saving, sign in with your new password.'}
      footer={
        <div className="text-center">
          <Link to="/login" className="btn btn-link">
            Back to sign in
          </Link>
        </div>
      }
    >
      <div className="store-auth-card__mobile-brand d-lg-none text-center mb-3">
        <img src={brandLogo} alt="" width={48} height={48} className="rounded-circle mb-2" />
        <p className="small text-muted mb-0">{brandName}</p>
      </div>
      <h2 className="store-auth-card__title h4">Choose a new password</h2>
      <form onSubmit={onSubmit} noValidate className="mt-3">
        {error ? (
          <div className="alert alert-danger py-2 small" role="alert">
            {error}
          </div>
        ) : null}
        {msg ? (
          <div className="alert alert-success py-2 small" role="status">
            {msg}
          </div>
        ) : null}
        <div className="checkout-field mb-3">
          <label className="checkout-field__label" htmlFor="new-pw">
            New password
          </label>
          <PasswordInput
            id="new-pw"
            className="form-control checkout-input"
            minLength={8}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
            required
          />
        </div>
        <div className="checkout-field mb-3">
          <label className="checkout-field__label" htmlFor="confirm-pw">
            Confirm password
          </label>
          <PasswordInput
            id="confirm-pw"
            className="form-control checkout-input"
            minLength={8}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            autoComplete="new-password"
            required
          />
        </div>
        <button type="submit" className="btn btn-primary rounded-pill w-100 store-cta" disabled={busy}>
          {busy ? 'Saving…' : 'Update password'}
        </button>
      </form>
    </AuthPageShell>
  );
}
